import React from 'react';
import { BadgePercent, CreditCard, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CorporateSLA: React.FC = () => {
  const { t } = useTranslation();

  const pillars = [
    {
      icon: <BadgePercent className="w-8 h-8 text-orange-500" />,
      title: t('sla.pillars.pricingTitle'),
      desc: t('sla.pillars.pricingDesc')
    },
    {
      icon: <CreditCard className="w-8 h-8 text-blue-500" />,
      title: t('sla.pillars.billingTitle'),
      desc: t('sla.pillars.billingDesc')
    },
    {
      icon: <Shield className="w-8 h-8 text-green-500" />,
      title: t('sla.pillars.securityTitle'),
      desc: t('sla.pillars.securityDesc')
    }
  ];

  const isEng = t('common.loading') === 'Loading...';

  return (
    <section id="corporate-sla" className="py-20 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white overflow-hidden relative">
      {/* Dynamic Background Glow */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="bg-orange-500/10 text-orange-400 border border-orange-500/20 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase inline-block mb-4">
            {t('sla.title')}
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold mb-6 leading-tight">
            {isEng ? "No more Urban Chaos." : "Chega de Caos Urbano."} <br />
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
              {isEng ? "Professionalize your Company Mobility." : "Profissionalize a Mobilidade da sua Empresa."}
            </span>
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            {t('sla.subtitle')}
          </p>
        </div>

        {/* SLA Matrix Table */}
        <div className="max-w-5xl mx-auto bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 p-8 md:p-12 shadow-2xl mb-20 water-float">
          <h3 className="text-xl font-bold text-center mb-8 text-gray-200">
              {isEng ? "Service Comparison Matrix" : "Matriz de Comparação de Serviço"}
          </h3>
          <div className="grid md:grid-cols-2 gap-8 relative">
            {/* Divider Line on Desktop */}
            <div className="hidden md:block absolute top-0 bottom-0 left-1/2 w-[1px] bg-white/10" />

            {/* Left: Informal Market */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                <AlertTriangle className="text-red-500 w-6 h-6 shrink-0" />
                <h4 className="text-lg font-bold text-red-400 uppercase tracking-wider">{t('sla.informalTitle')}</h4>
              </div>
              <ul className="space-y-4 text-gray-300 text-sm">
                <li className="flex items-start gap-3">
                  <span className="text-red-500 font-bold">✕</span>
                  <span>
                    <strong>{isEng ? "Abusive Dynamic Pricing: " : "Tarifa Dinâmica Abusiva: "}</strong>
                    {isEng ? "Prices fluctuate based on rain, traffic, or time of day without any logic." : "Preços flutuam de acordo com a chuva, trânsito ou hora do dia sem qualquer padrão."}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 font-bold">✕</span>
                  <span>
                    <strong>{isEng ? "Security Risk: " : "Risco de Segurança: "}</strong>
                    {isEng ? "Unidentified riders, no background checks, and non-maintained motorcycles." : "Condutores sem identificação, sem registo criminal verificado e motas sem manutenção."}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 font-bold">✕</span>
                  <span>
                    <strong>{isEng ? "Individual Billing: " : "Facturação Individual: "}</strong>
                    {isEng ? "Employees spend cash and demand complex manual refunds at the end of the month." : "Colaboradores gastam dinheiro físico e exigem reembolsos manuais complexos no final do mês."}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 font-bold">✕</span>
                  <span>
                    <strong>{isEng ? "No Tracking: " : "Sem Rastreamento: "}</strong>
                    {isEng ? "Impossible to know where packages or employees are located during delays." : "Impossível saber onde estão as encomendas ou onde os colaboradores estão localizados em caso de atraso."}
                  </span>
                </li>
              </ul>
            </div>

            {/* Right: MotoRush B2B */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                <CheckCircle className="text-green-500 w-6 h-6 shrink-0" />
                <h4 className="text-lg font-bold text-green-400 uppercase tracking-wider">{t('sla.officialTitle')}</h4>
              </div>
              <ul className="space-y-4 text-gray-300 text-sm">
                <li className="flex items-start gap-3">
                  <span className="text-green-500 font-bold">✓</span>
                  <span>
                    <strong>{isEng ? "Contractual Fixed Pricing: " : "Preço Fixo Contratual: "}</strong>
                    {isEng ? "Stable and predictable corporate rates, agreed upon by contract." : "Tarifas corporativas estáveis e previsíveis, acordadas em contrato."}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 font-bold">✓</span>
                  <span>
                    <strong>{isEng ? "Certified Riders: " : "Condutores Certificados: "}</strong>
                    {isEng ? "Uniformed professional riders, background checked, and constantly evaluated." : "Motoqueiros profissionais uniformizados, verificados criminalmente e sob constante avaliação."}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 font-bold">✓</span>
                  <span>
                    <strong>{isEng ? "Single Monthly Invoice: " : "Factura Única Mensal: "}</strong>
                    {isEng ? "Integrated ERP consolidating all company rides into a single post-paid corporate invoice." : "ERP integrado que consolida todas as viagens da empresa numa única factura pós-paga corporativa."}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 font-bold">✓</span>
                  <span>
                    <strong>{isEng ? "Fleet Telemetry: " : "Telemetria de Frota: "}</strong>
                    {isEng ? "Corporate management dashboard with real-time GPS location and route reports." : "Painel de gestão corporativo com localização GPS em tempo real e relatórios de rotas."}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* 3 Pillars Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {pillars.map((p, index) => (
            <div
              key={index}
              className={`bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-all duration-300 hover:-translate-y-1 shadow-md ${index % 2 === 0 ? 'water-float' : 'water-float-delayed'}`}
            >
              <div className="bg-white/5 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                {p.icon}
              </div>
              <h3 className="text-xl font-bold mb-3">{p.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CorporateSLA;
